<template>
  <modal-fullscreen
    :visible="visible"
    :title="title"
    width="650px"
    data-testid="document-form-modal"
    :destroy-on-close="true"
    @cancel="closeModal"
    v-if="visible"
  >
    <a-modal
      v-model="visibleCloseModal"
      title="Unsaved Changes Detected"
      width="650px"
      data-testid="document-form-unsaved-modal"
      :destroy-on-close="true"
    >
      <p>
        You have made changes to steps or placeholders that haven’t been saved. If you leave now, all unsaved changes
        will be lost. Are you sure you want to discard your changes?
      </p>
      <template slot="footer">
        <div class="flex justify-content-between" data-testid="document-form-unsaved-footer">
          <a-button @click="visibleCloseModal = false" data-testid="document-form-unsaved-continue-button">
            Continue Editing
          </a-button>
          <div class="flex gap-2">
            <a-button type="danger" @click="clearData" data-testid="document-form-unsaved-discard-button">
              Discard Changes
            </a-button>
            <a-button
              icon="save"
              type="primary"
              ghost
              :loading="loadingSave"
              data-testid="document-form-unsaved-save-close-button"
              @click="
                () => {
                  visibleCloseModal = false
                  saveDocument(true)
                }
              "
              >Save & Close</a-button
            >
            <a-button
              icon="save"
              type="primary"
              ghost
              :loading="loadingSave"
              data-testid="document-form-unsaved-save-button"
              @click="
                () => {
                  visibleCloseModal = false
                  saveDocument(false)
                }
              "
              >Save</a-button
            >
          </div>
        </div>
      </template>
    </a-modal>

    <div v-if="loadingDocument" class="loading-spin" style="height: 650px" data-testid="document-form-loading">
      <a-spin />
    </div>
    <template v-else>
      <document-preview
        v-if="this.document?.current_version && this.document.id"
        :version="this.document.current_version"
        :document_id="this.document.id"
        :visible.sync="visiblePreviewModal" />

      <a-form-model
        layout="horizontal"
        :model="documentForm"
        :rules="rules"
        :validateOnRuleChange="true"
        ref="documentFormRef"
        class="document-form"
        :label-col="{ span: 8 }"
        :wrapper-col="{ span: 16 }"
        data-testid="document-form"
      >
        <p class="form-description" data-testid="document-form-description">
          {{ documentDescription }}
        </p>

        <section class="document-form-section" data-testid="document-form-details-section">
          <div class="document-form-section__header">
            <h3>Document details</h3>
            <p>
              Name the document, choose how its content is delivered, and set how often people have to complete it
              again.
            </p>
          </div>

          <div class="document-form-section__body">
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Name"
              prop="name"
              extra="What people see in the library, in sequences, and on their profile. Use the name your team already uses — TB Test, Meal Break Waiver."
            >
              <a-input v-model="documentForm.name" placeholder="Enter name" data-testid="document-form-name-input" />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Type"
              prop="type"
              extra="How the content is delivered: a file to read, a video to watch, text written here, or a quiz to pass. The type decides what an associate actually does to complete the document."
            >
              <custom-select
                :multiple="false"
                :value.sync="documentForm.type"
                placeholder="Select Type"
                :options="types"
                name="name"
                optionKey="id"
                data-testid="document-form-type-select"
              />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Group"
              extra="Folders for your library. Once you have hundreds of documents, groups are how you find the right one — Certifications, Company Policies, Tax Forms."
            >
              <custom-select
                :multiple="false"
                :value.sync="documentForm.group"
                placeholder="Select Group"
                :options="groups"
                name="name"
                optionKey="id"
                data-testid="document-form-group-select"
              />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Expiration Period"
              extra="How long until the associate has to complete this document again — set 1 year for anything renewed annually. The count starts the day each person signs, so everyone has their own due date. Leave it at 0 if it never needs redoing."
            >
              <a-input-group compact>
                <a-input-number
                  style="width: 30%"
                  v-model="documentForm.expiration_period"
                  :min="0"
                  :max="364"
                  placeholder="Enter period"
                  data-testid="document-form-expiration-period-input"
                />
                <a-select
                  style="width: 70%"
                  v-model="expirationType"
                  :dropdown-match-select-width="false"
                  data-testid="document-form-expiration-type-select"
                >
                  <a-select-option value="day"> Day </a-select-option>
                  <a-select-option value="year"> Year </a-select-option>
                </a-select>
              </a-input-group>
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Update Period"
              extra="A reminder for you, not for associates. Set it when the document itself goes out of date — tax forms and similar paperwork get new versions every year, and this is when you check whether yours is still current."
            >
              <a-date-picker
                v-model="documentForm.update_period"
                placeholder="Update Period"
                class="w-full"
                :allowClear="true"
                :format="visualDateFormat"
                data-testid="document-form-update-period-input"
              />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Required in Company"
              prop="required_in_project"
              extra="Turn this on to track who has this document. Everyone in Target shows up in Documentation Compliance — signed, missing, or expired — so you can see coverage at a glance."
            >
              <a-switch v-model="documentForm.required_in_project" data-testid="document-form-required-switch" />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Request Automatically"
              prop="request_automatically"
              extra="ePeople asks people for this document on its own — those who never signed it, and anyone whose copy expired. Turn it on to collect the document across the company instead of chasing people one by one."
            >
              <a-switch
                v-model="documentForm.request_automatically"
                data-testid="document-form-request-automatically-switch"
              />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Critical for Work"
              prop="critical"
              extra="Same as Request Automatically, but the document is not optional — turn both on. ePeople keeps asking for it, and if the associate still has not signed it, or their copy expired, their work is blocked until they do. Use it when nobody should be working without this document on file."
            >
              <a-switch v-model="documentForm.critical" data-testid="document-form-critical-switch" />
            </a-form-model-item>
          </div>
        </section>

        <section v-if="documentForm.type" class="document-form-section" data-testid="document-form-content-section">
          <div class="document-form-section__header">
            <h3>Content</h3>
            <p>Fill in the material associates will read, watch, complete, or acknowledge.</p>
          </div>

          <div v-if="documentForm.type === 'pdf'" data-testid="document-form-pdf-content">
            <document-pdf-view
              :file="documentForm?.body?.file?.url"
              :is-placeholders-changes.sync="isPlaceholdersChanges"
              ref="pdfRef"
              @preview="visiblePreviewModal = true"
              @save="setFileId"
            />
          </div>

          <div v-else-if="documentForm.type === 'video'" data-testid="document-form-video-content">
            <document-video-view :file="documentForm?.body?.file?.url" @save="setFileId" />
          </div>

          <div v-else-if="documentForm.type === 'survey'" data-testid="document-form-survey-content">
            <document-survey-view ref="surveyRef" :survey="documentForm?.body?.survey" @save="setSurvey" />
          </div>
          <div v-else data-testid="document-form-text-content">
            <document-text-view ref="textRef" :text="documentForm?.body?.items ?? []" @save="setText" />
          </div>
        </section>

        <section class="document-form-section" data-testid="document-form-target-section">
          <div class="document-form-section__header">
            <h3>Target</h3>
            <p>Choose who this document applies to. Leave a field empty to include everyone in it.</p>
          </div>

          <div class="document-form-section__body">
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Payment Types"
              prop="scope.payment_types"
              extra="Which employment types get this document — Employees, Contractors, or both. Leave it empty to include every payment type."
            >
              <custom-select
                :multiple="true"
                :value.sync="paymentTypeProxy"
                placeholder="Payment Types"
                :options="paymentTypes"
                name="name"
                optionKey="id"
                data-testid="document-form-payment-types-select"
              />
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Positions"
              prop="scope.roles"
              extra="Which positions get this document — CNA, LVN, RN, and so on. Leave it empty to include every position."
            >
              <custom-select
                :multiple="true"
                :value.sync="roleProxy"
                placeholder="Position"
                :options="roles"
                name="name"
                optionKey="id"
                data-testid="document-form-positions-select"
              />
            </a-form-model-item>
            <a-form-model-item
              label=" "
              :colon="false"
              label-align="left"
              extra="Flips the selection: everyone gets the document except the positions and payment types picked above. Use it when the exceptions are shorter than the list."
            >
              <a-checkbox
                :checked="excludeRolesMode"
                @change="handleExcludeRolesModeChange"
                data-testid="document-form-exclude-positions-checkbox"
              >
                Target All Except Selected
              </a-checkbox>
            </a-form-model-item>

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Legal Entities"
              prop="scope.legal_entities"
              extra="Which legal entities’ contractors get this document. Select the entities this document applies to, or turn on “Target All Except Selected” to exclude their contractors."
            >
              <custom-select
                :multiple="true"
                :value.sync="agencyProxy"
                placeholder="Legal Entities"
                :options="legalEntities"
                name="name"
                optionKey="id"
                data-testid="document-form-legal-entities-select"
              />
            </a-form-model-item>
            <a-form-model-item
              label=" "
              :colon="false"
              label-align="left"
              extra="Flips the selection: everyone gets the document except the positions and payment types picked above. Use it when the exceptions are shorter than the list."
            >
              <a-checkbox
                :checked="excludeLegalEntitiesMode"
                @change="handleExcludeLegalEntitiesModeChange"
                data-testid="document-form-exclude-legal-entities-checkbox"
              >
                Target All Except Selected
              </a-checkbox>
            </a-form-model-item>

            <a-form-model-item
              v-if="hasContractorPaymentType"
              :colon="false"
              label-align="left"
              label="Contractor Types"
              prop="scope.contractor_sub_types"
              extra="Which contractors get this document—independent contractors, agency staff, or marketplace staff. Select the types this document applies to."
            >
              <custom-select
                :multiple="true"
                :value.sync="contractorSubTypeProxy"
                placeholder="Contractor Types"
                :options="contractorSubTypes"
                name="name"
                optionKey="id"
                data-testid="document-form-contractor-types-select"
              />
            </a-form-model-item>
          </div>
        </section> </a-form-model
    ></template>

    <template slot="footer"
      ><div class="flex justify-content-between" data-testid="document-form-footer">
        <a-button @click="closeModal" data-testid="document-form-close-button">Close</a-button>
        <div class="flex gap-2">
          <a-button
            icon="save"
            type="primary"
            ghost
            :loading="loadingSave"
            data-testid="document-form-save-close-button"
            @click="saveDocument(true)"
            >Save & Close</a-button
          >
          <a-button
            icon="save"
            type="primary"
            ghost
            :loading="loadingSave"
            data-testid="document-form-save-button"
            @click="saveDocument(false)"
          >
            Save
          </a-button>
        </div>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import { DOCUMENT_TYPE } from '@/store/modules/document/constants'
import { CONTRACTOR_SUB_TYPES } from '@/store/modules/legal-entity/constants'
import {
  Button,
  Checkbox,
  DatePicker,
  FormModel,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Spin,
  Switch
} from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import DocumentFormPdf from './document-form-pdf.vue'
import DocumentFormVideo from './document-form-video.vue'
import DocumentFormSurvey from './document-form-survey.vue'
import DocumentFormText from './document-form-text.vue'
import moment from 'moment-timezone'
import CustomSelect from '@/components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import { normalizePaymentTypes as normalizePaymentTypeValues } from '@/utils/payment-types'
import DocumentPreview from './document-preview.vue'
import util from '@/util'

const { mapActions: documentActions, mapState: documentState } = createNamespacedHelpers('document')

const initialForm = {
  group: undefined,
  type: undefined,
  name: undefined,
  initial: undefined,
  modifiable: true,
  expiration_period: 0,
  update_period: undefined,
  required_in_project: false,
  request_automatically: false,
  critical: false,
  scope: {
    legal_entities: [],
    exclude_legal_entities: [],
    contractor_sub_types: [],
    exclude_contractor_sub_types: [],
    roles: [],
    exclude_roles: [],
    payment_types: [],
    exclude_payment_types: []
  }
}

const DELAYED_DOCUMENT_FLAGS_MESSAGE = "Changes saved. They'll take effect within 15 minutes."
const DELAYED_DOCUMENT_FLAGS = ['critical', 'required_in_project', 'request_automatically']
const HIDDEN_DOCUMENT_TYPE_OPTIONS = ['text']

export default {
  components: {
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-spin': Spin,
    'a-modal': Modal,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input': Input,
    'a-input-number': InputNumber,
    'a-date-picker': DatePicker,
    'a-input-group': Input.Group,
    'a-switch': Switch,
    'document-preview': DocumentPreview,
    'custom-select': CustomSelect,
    'document-pdf-view': DocumentFormPdf,
    'document-video-view': DocumentFormVideo,
    'document-survey-view': DocumentFormSurvey,
    'document-text-view': DocumentFormText
  },
  props: {
    document_id: {
      type: Number,
      default: undefined
    },
    version_number: {
      type: Number,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    },
    initial_values: {
      type: Object,
      default: undefined
    }
  },
  emits: ['edit'],
  data() {
    return {
      loadingDocument: false,
      loadingSave: false,

      documentForm: deepCopy(initialForm),
      initialDocumentForm: deepCopy(initialForm),

      rules: {
        name: [
          {
            required: true,
            message: 'Name can not be empty',
            trigger: 'change'
          },
          {
            pattern: /^[^/\\]*$/,
            message: 'Symbols / and \\ are not allowed',
            trigger: 'change'
          }
        ],
        type: [
          {
            required: true,
            message: 'Type can not be empty',
            trigger: 'change'
          }
        ]
      },

      expirationType: 'day',

      visibleCloseModal: false,
      visiblePreviewModal: false,
      signatureKey: 0,

      isPlaceholdersChanges: false,

      roles: this.$store.state.applicationState.projectRoles,
      paymentTypes: this.$store.state.applicationState.paymentTypes.map(item => ({
        name: item.name,
        id: item.name.toLowerCase()
      })),
      legalEntities: this.$store.state.applicationState.projectLegalEntities,
      contractorSubTypes: CONTRACTOR_SUB_TYPES,
      excludeRolesMode: false,
      excludeLegalEntitiesMode: false,
      isScopeHydrating: false,
      documentFlagsAlertShown: false
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        if (newVal) {
          this.isScopeHydrating = true
          this.documentFlagsAlertShown = false
          if (!this.groups.length) this.fetchGroups()
          if (!this.placeholders.length) this.fetchPlaceholders()

          this.documentForm = deepCopy(initialForm)
          this.excludeRolesMode = false
          this.excludeLegalEntitiesMode = false

          if (!this.document_id && this.initial_values) {
            this.applyInitialValues()
          }

          this.initialDocumentForm = deepCopy(this.documentForm)
          this.isScopeHydrating = false

          if (this.document_id) {
            this.fetchDocument()
          }
        }
      },
      immediate: true
    },
    document_id(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.visible) {
        this.fetchDocument()
      }
    },
    'documentForm.scope.payment_types': {
      handler(newVal, oldVal) {
        if (this.isScopeHydrating) return

        const hasContractor = newVal?.includes('contractor')
        const hadContractor = oldVal?.includes('contractor')

        if (!hasContractor) {
          this.documentForm.scope.contractor_sub_types = []
          return
        }

        if (!hadContractor) {
          this.documentForm.scope.contractor_sub_types = this.contractorSubTypes.map(item => item.id)
        }
      },
      deep: true
    }
  },
  computed: {
    types() {
      return Object.entries(DOCUMENT_TYPE)
        .filter(([key]) => !HIDDEN_DOCUMENT_TYPE_OPTIONS.includes(key) || this.initial_values?.type === key)
        .map(([key, value]) => ({ id: key, name: value.title }))
    },

    ...documentState({
      groups: state => state.groups,
      document: state => state.document,
      placeholders: state => state.placeholders
    }),

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },

    title() {
      return this.document_id ? 'Update Document' : 'Create Document'
    },

    documentDescription() {
      switch (this.documentForm.type) {
        case 'pdf':
          return 'Upload a document your company already has — a policy, a form, an agreement. It goes into your library and can be added to any sequence, so you upload it once and use it wherever it is required. People read it and sign it as a step in their sequence. You can replace the file later; each change is saved as a new version.'
        case 'video':
          return 'Add a video people watch and then sign to confirm they have seen it. It goes into your library and can be added to any sequence, so the same video can serve onboarding, training, or anything else. The signature is stored against the version they saw, so you can always show what was in effect at the time. You can replace the video later; each change is saved as a new version.'
        case 'survey':
          return 'Build a quiz that checks whether people understood the material. Add questions, mark the correct answers, and set how many are needed to pass. It goes into your library and can be added to any sequence — useful when a signature alone is not enough to show someone learned something. You can change the questions later; each change is saved as a new version.'
        default:
          return 'You are creating a document for your company library. Once saved, it can be added to any sequence — onboarding, training, or departure — and reused as many times as you need. Choose a type below: upload a file, add a video, write the text here, or build a quiz. You can change the document later; each change is saved as a new version.'
      }
    },

    isPdfOrText() {
      return this.documentForm.type === 'pdf' || this.documentForm.type === 'text'
    },
    hasContractorPaymentType() {
      return this.paymentTypeProxy.includes('contractor')
    },

    hasUnsavedChanges() {
      return (
        !this.isPlaceholdersChanges && JSON.stringify(this.initialDocumentForm) === JSON.stringify(this.documentForm)
      )
    },

    roleProxy: {
      get() {
        const roles = this.excludeRolesMode ? this.documentForm.scope.exclude_roles : this.documentForm.scope.roles
        return roles ? roles.map(item => item.id) : undefined
      },
      set(value) {
        const mappedValue = value.map(item => ({ id: item }))
        if (this.excludeRolesMode) {
          this.documentForm.scope.exclude_roles = mappedValue
          this.documentForm.scope.roles = []
          return
        }
        this.documentForm.scope.roles = mappedValue
        this.documentForm.scope.exclude_roles = []
      }
    },
    paymentTypeProxy: {
      get() {
        return this.normalizePaymentTypes(this.documentForm.scope.payment_types)
      },
      set(value) {
        this.documentForm.scope.payment_types = this.normalizePaymentTypes(value)
      }
    },

    agencyProxy: {
      get() {
        const legalEntities = this.excludeLegalEntitiesMode
          ? this.documentForm.scope.exclude_legal_entities
          : this.documentForm.scope.legal_entities
        return legalEntities || undefined
      },
      set(value) {
        if (this.excludeLegalEntitiesMode) {
          this.documentForm.scope.exclude_legal_entities = value
          this.documentForm.scope.legal_entities = []
          return
        }
        this.documentForm.scope.legal_entities = value
        this.documentForm.scope.exclude_legal_entities = []
      }
    },
    contractorSubTypeProxy: {
      get() {
        return this.documentForm.scope.contractor_sub_types?.length
          ? this.documentForm.scope.contractor_sub_types
          : undefined
      },
      set(value) {
        this.documentForm.scope.contractor_sub_types = value
      }
    }
  },
  methods: {
    ...documentActions([
      'getDocumentsGroups',
      'createDocument',
      'updateDocument',
      'getProjectDocument',
      'exportDocument',
      'getDocumentPlaceholders'
    ]),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    hasDelayedDocumentFlagsChanges() {
      const initial = this.documentForm?.initial

      if (!initial) return false

      return DELAYED_DOCUMENT_FLAGS.some(field => Boolean(this.documentForm?.[field]) !== Boolean(initial?.[field]))
    },
    showDelayedDocumentFlagsAlert() {
      if (this.documentFlagsAlertShown || !this.hasDelayedDocumentFlagsChanges()) return

      this.documentFlagsAlertShown = true
      this.showNotification('info', DELAYED_DOCUMENT_FLAGS_MESSAGE)
    },
    handleExcludeRolesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeRolesMode) return

      this.excludeRolesMode = checked
      if (checked) {
        this.documentForm.scope.exclude_roles = [...this.documentForm.scope.roles]
        this.documentForm.scope.roles = []
        return
      }
      this.documentForm.scope.roles = [...this.documentForm.scope.exclude_roles]
      this.documentForm.scope.exclude_roles = []
    },
    handleExcludeLegalEntitiesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeLegalEntitiesMode) return

      this.excludeLegalEntitiesMode = checked
      if (checked) {
        this.documentForm.scope.exclude_legal_entities = [...this.documentForm.scope.legal_entities]
        this.documentForm.scope.legal_entities = []
        return
      }
      this.documentForm.scope.legal_entities = [...this.documentForm.scope.exclude_legal_entities]
      this.documentForm.scope.exclude_legal_entities = []
    },
    normalizeScope(scope) {
      return {
        ...scope,
        roles: this.excludeRolesMode ? [] : (scope.roles ?? []),
        exclude_roles: this.excludeRolesMode ? (scope.exclude_roles ?? []) : [],
        legal_entities: this.excludeLegalEntitiesMode ? [] : (scope.legal_entities ?? []),
        exclude_legal_entities: this.excludeLegalEntitiesMode ? (scope.exclude_legal_entities ?? []) : [],
        payment_types: this.normalizePaymentTypes(scope.payment_types),
        exclude_payment_types: scope.exclude_payment_types ?? [],
        contractor_sub_types: this.hasContractorPaymentType ? (scope.contractor_sub_types ?? []) : [],
        exclude_contractor_sub_types: scope.exclude_contractor_sub_types ?? []
      }
    },
    normalizePaymentTypes(paymentTypes = []) {
      return normalizePaymentTypeValues(paymentTypes, this.$store.state.applicationState.paymentTypes)
    },
    applyInitialValues() {
      const type = this.initial_values.type
      if (this.types.some(item => item.id === type)) {
        this.documentForm.type = type
      }

      if (!this.initial_values.scope) return

      const scope = deepCopy(this.initial_values.scope)

      this.documentForm.scope = {
        roles: (scope.roles ?? []).map(item => ({ id: item.id ?? item })),
        exclude_roles: (scope.exclude_roles ?? []).map(item => ({ id: item.id ?? item })),
        payment_types: this.normalizePaymentTypes(scope.payment_types ?? []),
        exclude_payment_types: this.normalizePaymentTypes(scope.exclude_payment_types ?? []),
        legal_entities: (scope.legal_entities ?? []).map(item => item.id ?? item),
        exclude_legal_entities: (scope.exclude_legal_entities ?? []).map(item => item.id ?? item),
        contractor_sub_types: (scope.contractor_sub_types ?? []).map(item => item.id ?? item),
        exclude_contractor_sub_types: (scope.exclude_contractor_sub_types ?? []).map(item => item.id ?? item)
      }
      this.excludeRolesMode = !!this.documentForm.scope.exclude_roles.length && !this.documentForm.scope.roles.length
      this.excludeLegalEntitiesMode =
        !!this.documentForm.scope.exclude_legal_entities.length && !this.documentForm.scope.legal_entities.length
    },

    closeModal() {
      if (!this.hasUnsavedChanges) {
        this.visibleCloseModal = true
      } else {
        this.clearData()
      }
    },

    clearData() {
      this.documentForm = deepCopy(initialForm)
      this.excludeRolesMode = false
      this.excludeLegalEntitiesMode = false
      this.documentFlagsAlertShown = false
      this.$emit('update:visible', false)
      this.$emit('update:document_id', undefined)
      this.$emit('update:version_number', undefined)
      this.visibleCloseModal = false
      this.isPlaceholdersChanges = false
    },

    setFileId(id) {
      this.documentForm = {
        ...this.documentForm,
        body: {
          file: {
            id: id
          }
        }
      }
    },

    setSurvey(survey) {
      this.documentForm = {
        ...this.documentForm,
        body: {
          survey: {
            ...survey
          }
        }
      }
    },

    setText(items) {
      this.documentForm = {
        ...this.documentForm,
        body: {
          items
        }
      }
    },

    setSignatureCheckboxValue(e) {
      this.signatureCheckboxChecked = e
    },

    async fetchGroups() {
      try {
        await this.getDocumentsGroups()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async saveDocument(close) {
      this.$refs.documentFormRef.validate(async valid => {
        if (!valid) return

        this.loadingSave = true

        try {
          if (this.documentForm.type === 'pdf') {
            await this.$refs.pdfRef.savePdf()
          } else if (this.documentForm.type === 'survey') {
            this.$refs.surveyRef.saveQuestions()
          } else if (this.documentForm.type === 'text') {
            this.$refs.textRef.splitTextByPlaceholders()
          }

          const expiration_period =
            this.expirationType === 'day'
              ? this.documentForm.expiration_period * 24 * 60
              : this.documentForm.expiration_period * 365 * 24 * 60

          const documentForSave = { ...this.documentForm }
          delete documentForSave.initial

          const payload = {
            document: {
              ...documentForSave,
              scope: this.normalizeScope(this.documentForm.scope),
              expiration_period,
              update_period: this.documentForm.update_period
                ? moment(this.documentForm.update_period)
                    .utc()
                    .startOf('day')
                    .tz(this.$store.state.applicationState.currentProject.timezone)
                    .toISOString()
                : undefined,
              group: this.groups.find(item => item.id === this.documentForm.group)
            }
          }

          const document = this.document_id ? await this.updateDocument(payload) : await this.createDocument(payload)

          this.showNotification(
            'success',
            'Info',
            `Document has been ${this.document_id ? 'updated' : 'created'} successfully`
          )
          this.showDelayedDocumentFlagsAlert()

          this.updateDocumentData()

          this.$emit('edit', document, this.document_id ? 'edit' : 'create')
          this.$emit('update:document_id', document.id)

          if (close) {
            this.clearData()
          }
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        } finally {
          this.loadingSave = false
        }
      })
    },

    updateDocumentData() {
      this.isScopeHydrating = true
      this.documentForm = deepCopy({
        ...this.document,
        request_automatically: this.document.request_automatically ?? false,
        group: this.document?.group?.id ?? undefined
      })

      this.expirationType = this.document.expiration_period / 60 / 24 > 364 ? 'year' : 'day'

      this.documentForm.expiration_period =
        this.expirationType === 'day'
          ? this.document.expiration_period / 60 / 24
          : this.document.expiration_period / 60 / 24 / 365

      this.documentForm.scope.payment_types = this.normalizePaymentTypes(this.document.scope.payment_types)
      this.documentForm.scope.legal_entities = (this.document.scope.legal_entities ?? []).map(item => item.id ?? item)
      this.documentForm.scope.exclude_legal_entities = (this.document.scope.exclude_legal_entities ?? []).map(
        item => item.id ?? item
      )
      this.documentForm.scope.contractor_sub_types = this.hasContractorPaymentType
        ? (this.document.scope.contractor_sub_types ?? []).map(item => item.id ?? item)
        : []
      this.documentForm.scope.exclude_roles = (this.document.scope.exclude_roles ?? []).map(item => ({
        id: item.id ?? item
      }))
      this.excludeRolesMode = !!this.documentForm.scope.exclude_roles.length && !this.documentForm.scope.roles.length
      this.excludeLegalEntitiesMode =
        !!this.documentForm.scope.exclude_legal_entities.length && !this.documentForm.scope.legal_entities.length

      this.initialDocumentForm = deepCopy(this.documentForm)
      this.isScopeHydrating = false
    },

    async fetchDocument() {
      try {
        this.loadingDocument = true

        const payload = {
          document_id: this.document_id,
          version_number: this.version_number
        }

        await this.getProjectDocument(payload)

        this.updateDocumentData()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingDocument = false
      }
    },

    async fetchPlaceholders() {
      try {
        await this.getDocumentPlaceholders()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async downloadPreview() {
      try {
        const payload = {
          top_level_object_id: this.document_id,
          type: 'project_document',
          export_type: 'download'
        }

        const { file_url } = await this.exportDocument(payload)

        util.downloadFile(file_url)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingPreview = false
      }
    }
  }
}
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 0;
}

::v-deep .ant-modal-body {
  padding-top: 0;
  min-height: 200px;
  max-height: 800px;
  overflow-y: scroll;
}

h3 {
  margin: 0;
}

.document-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-description {
  margin: 0;
  color: var(--text-color-secondary);
  line-height: 1.5;
}

.document-form-section {
  padding: 16px;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.02);
}

.document-form-section__header {
  margin-bottom: 16px;
}

.document-form-section__header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.document-form-section__header p {
  margin: 4px 0 0;
  color: var(--text-color-secondary);
  line-height: 1.45;
}

.document-form-section__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ant-card {
  height: fit-content;
  border-radius: 10px;
}

::v-deep .ant-input-number {
  width: 30% !important;
}
</style>
